import React, { useState } from "react";
import SvgIcon from "../../../../parts/SvgIcon";
import { getDisplayDate } from "../../../../utils/date-helpers";

const FoodLog = ({ stats, isPeriod, isCalendar = false }) => {
  const hasFoodLogData = Boolean(stats.foodLog);

  if (!hasFoodLogData) {
    return (
      <NoDataState stats={stats} isPeriod={isPeriod} isCalendar={isCalendar} />
    );
  }

  if (isPeriod) return <PeriodFoodTables stats={stats} />;

  const source = stats.foodLog.source;

  if (source === "macrosFirst")
    return <MacrosFirstTable stats={stats} isCalendar={isCalendar} />;
  else if (source === "cronometer")
    return <CronometerTable stats={stats} isCalendar={isCalendar} />;
  else
    return (
      <NoDataState stats={stats} isPeriod={isPeriod} isCalendar={isCalendar} />
    );
};

const NoDataState = ({ stats, isPeriod, isCalendar }) => {
  return (
    <div className="nutrition-summary-head">
      <div className="summary-title">
        {isPeriod ? (
          <span>
            {getDisplayDate(stats.startOfWeek)} to{" "}
            {getDisplayDate(stats.endOfWeek)}
          </span>
        ) : (
          !isCalendar && <span>{getDisplayDate(stats.utcDate, true)}</span>
        )}
      </div>
      <div className="empty-state">
        <span className="font-bold">No food data to show</span>
      </div>
    </div>
  );
};

const DisplayDate = ({ date, source, isCalendar = false }) => {
  const sourceString = {
    cronometer: "Cronometer",
    macrosFirst: "MacrosFirst",
  };

  return (
    <table className="report-table">
      <thead>
        <tr>
          <th>
            {!isCalendar && (
              <p className="font-bold">{getDisplayDate(date, true)}</p>
            )}
            <div className="chip info">Synced via {sourceString[source]}</div>
          </th>

          <th>
            <p className="protein">Protein</p>
            <p className="st1">(g)</p>
          </th>
          <th>
            <p className="carbs">Carbs</p>
            <p className="st1">(g)</p>
          </th>
          <th>
            <p className="fat">Fat</p>
            <p className="st1">(g)</p>
          </th>
          <th>
            <p className="font-bold">Fiber</p>
            <p className="st1">(g)</p>
          </th>
          <th>
            <p className="font-bold">kCal</p>
          </th>
        </tr>
      </thead>
    </table>
  );
};

const PeriodFoodTables = ({ stats }) => {
  const { foodLog } = stats;

  const sortedLog = [...foodLog].sort((a, b) => {
    return b.date - a.date;
  });

  return sortedLog.map((log, index) => {
    const { date, foodLog } = log;
    const { source } = foodLog;

    const foodStats = { foodLog, utcDate: date };

    if (source === "macrosFirst")
      return (
        <MacrosFirstTable
          stats={foodStats}
          key={`macrosfirst-${index}-table`}
        />
      );
    else if (source === "cronometer")
      return (
        <CronometerTable stats={foodStats} key={`cronometer-${index}-table`} />
      );
    else
      return (
        <div className="empty-state" key={`no-data-${index}`}>
          <span>{getDisplayDate(date, true)}</span>
          <span className="font-bold">No food data to show</span>
        </div>
      );
  });
};

// MacrosFirst always has at least 4 objects in the meals array
// Even when there are no food in the object
// But the meals inside the food object can be empty
// It also includes the following properties on an instance of the foods array (eg, an egg)
// name: "Egg";
// servingName: "large egg";
// servingSize: 3;
// fats:  15;
// proteins: 18;
// kcal: 210;

const macrosFirstOrder = ["Breakfast", "Lunch", "Dinner", "Snack"];

const MacrosFirstTable = ({ stats, isCalendar }) => {
  const { meals } = stats.foodLog;

  const orderedMeals = meals.sort((a, b) => {
    const indexA = macrosFirstOrder.indexOf(a.name);
    const indexB = macrosFirstOrder.indexOf(b.name);

    // If the name is in the order array, sort according to its index.
    // Otherwise, push it to the end (return a large index).
    return (
      (indexA === -1 ? macrosFirstOrder.length : indexA) -
      (indexB === -1 ? macrosFirstOrder.length : indexB)
    );
  });

  return (
    <article className="foodlog-container">
      <DisplayDate
        date={stats.utcDate}
        source={"macrosFirst"}
        isCalendar={isCalendar}
      />
      {orderedMeals.map((meal, index) => {
        const [showBody, setShowBody] = useState(true);

        return (
          <section
            role="table-wrapper"
            key={`macrosfirst-${stats.utcDate}-${meal.name}-${index}`}
          >
            <table className="report-table">
              <thead
                onClick={() => {
                  setShowBody(!showBody);
                }}
              >
                <tr>
                  <th>
                    <SvgIcon
                      src={"/img/new-icons/arrow-square-right.svg"}
                      classNames={showBody ? "rotated" : ""}
                    />{" "}
                    {meal.name}
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.proteins)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.carbs)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.fats)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.fibers)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.kcal)}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {showBody &&
                  meal.foods.map((food, index) => (
                    <tr
                      key={`macrosfirst-${stats.utcDate}-${food.name}-${index}`}
                    >
                      <td>
                        <div>
                          <p>{food.name}</p>
                          <p className="p5">
                            {food.servingSize} {food.servingName}
                          </p>
                        </div>
                      </td>
                      <td>{Math.round(food.proteins || 0)}</td>
                      <td>{Math.round(food.carbs || 0)}</td>
                      <td>{Math.round(food.fats || 0)}</td>
                      <td>{Math.round(food.fibers || 0)}</td>
                      <td>{Math.round(food.kcal || 0)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </article>
  );
};

// Cronometer only stores the registered meals in the meals array
// Meaning, it will be only populated if the user has registered a meal
// So possibly it will skip meals if the user has, so if you are reading this in the future, it's not a bug, it's a feature
// It includes the following properties on an instance of the food array (eg, an omelet)
// name: "Omelet, Plain";
// servingName: "1.0 - large egg used - 1 whole egg or 2 egg whites";
const cronometerOrder = ["Breakfast", "Lunch", "Dinner", "Snack"];

const CronometerTable = ({ stats, isCalendar }) => {
  const { meals } = stats.foodLog;

  const orderedMeals = meals.sort((a, b) => {
    const indexA = cronometerOrder.indexOf(a.name);
    const indexB = cronometerOrder.indexOf(b.name);

    // If the name is in the order array, sort according to its index.
    // Otherwise, push it to the end (return a large index).
    return (
      (indexA === -1 ? cronometerOrder.length : indexA) -
      (indexB === -1 ? cronometerOrder.length : indexB)
    );
  });

  return (
    <article className="foodlog-container">
      <DisplayDate
        date={stats.utcDate}
        source={"cronometer"}
        isCalendar={isCalendar}
      />
      {orderedMeals.map((meal, index) => {
        const [showBody, setShowBody] = useState(true);

        return (
          <section
            role="table-wrapper"
            key={`cronometer-${stats.utcDate}-${meal.name}-${index}`}
          >
            <table className="report-table">
              <thead
                onClick={() => {
                  setShowBody(!showBody);
                }}
              >
                <tr>
                  <th>
                    <SvgIcon
                      src={"/img/new-icons/arrow-square-right.svg"}
                      classNames={showBody ? "rotated" : ""}
                    />{" "}
                    {meal.name}
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.proteins)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.carbs)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.fats)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.fibers)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.kcal)}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {showBody &&
                  meal.foods.map((food, index) => (
                    <tr
                      key={`cronometer-${stats.utcDate}-${food.name}-${index}`}
                    >
                      <td>{food.name}</td>
                      <td colSpan={5} className="serving">
                        <p className="p5">{food.servingName}</p>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </article>
  );
};

export default FoodLog;
